import type { Typed } from 'services/api/fields/core';

import { Attr, AttrOptions } from './Attr';
import { Classification, ClassificationOptions } from './Classification';
import { Client, ClientOptions } from './Client';
import { Group, GroupOptions } from './Group';

export interface AttrValueOptions {
  client?: ClientOptions;
  attr?: AttrOptions;
  associatedClassifications?: ClassificationOptions[];
  associatedGroups?: GroupOptions[];
  [key: string]: any;
}

function getDefaults(): AttrValueOptions {
  return {
    id: '',
    createdAt: null,
    createdBy: null,
    name: '',
    displayName: '',
    visible: false,
    active: true,
    associatedClassifications: [],
    associatedGroups: [],
  } as AttrValueOptions;
}

// Forward declare so _AttrValue's constructor can return the infused type

let AttrValue: new <T extends AttrValueOptions>(options: T) => Typed<_AttrValue<T>, T>;

class _AttrValue<T extends AttrValueOptions = AttrValueOptions> {
  client!: Client<NonNullable<T['client']>> | null;
  attr!: Attr<NonNullable<T['attr']>> | null;
  associatedClassifications!: Classification<NonNullable<T['associatedClassifications']>[number]>[];
  associatedGroups!: Group<NonNullable<T['associatedGroups']>[number]>[];

  constructor(options: T) {
    const opts = { ...getDefaults(), ...options };
    Object.assign(this, opts);

    this.client = opts.client ? new Client<NonNullable<T['client']>>(opts.client) : null;
    this.attr = opts.attr ? new Attr<NonNullable<T['attr']>>(opts.attr) : null;
    this.associatedClassifications = (opts.associatedClassifications ?? []).map(
      (classification) => new Classification<NonNullable<T['associatedClassifications']>[number]>(classification)
    );
    this.associatedGroups = (opts.associatedGroups ?? []).map(
      (group) => new Group<NonNullable<T['associatedGroups']>[number]>(group)
    );
  }
}

// eslint-disable-next-line prefer-const
AttrValue = _AttrValue as typeof AttrValue;

export default AttrValue;
export { AttrValue };
export type AttrValue<T extends AttrValueOptions = AttrValueOptions> = _AttrValue<T> & Omit<T, keyof _AttrValue<T>>;
